import React, { useState, useEffect } from 'react';
import { studentUniformAPI } from '../../services/api';
import Swal from 'sweetalert2';
import './AddStudentModal.css';

/**
 * Modal for adding new students to the uniform allocation screen
 * Flow:
 * 1. User enters IC number
 * 2. System validates and looks up student in Student Master
 * 3. Display student info and current enrollment (if any)
 * 4. User selects Tahun and Kelas for their school
 * 5. Confirm and add student to grid
 */
const AddStudentModal = ({ isOpen, onClose, onAddStudent, enrollYear }) => {
  // State management
  const [icNumber, setIcNumber] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [studentFound, setStudentFound] = useState(null);
  const [selectedTahun, setSelectedTahun] = useState('');
  const [selectedKelas, setSelectedKelas] = useState('');
  const [tahunOptions, setTahunOptions] = useState([]);
  const [kelasOptions, setKelasOptions] = useState([]);
  const [error, setError] = useState('');

  // Load Tahun and Kelas options when modal opens
  useEffect(() => {
    if (isOpen) {
      loadDropdownOptions();
    }
  }, [isOpen]);

  // Load Tahun and Kelas dropdown options
  const loadDropdownOptions = async () => {
    try {
      const [tahunResponse, kelasResponse] = await Promise.all([
        studentUniformAPI.getTahunList(),
        studentUniformAPI.getKelasList()
      ]);

      if (tahunResponse.success && tahunResponse.data) {
        setTahunOptions(tahunResponse.data);
      }

      if (kelasResponse.success && kelasResponse.data) {
        setKelasOptions(kelasResponse.data);
      }
    } catch (err) {
      console.error('Error loading dropdown options:', err);
      Swal.fire({
        icon: 'error',
        title: 'Ralat',
        text: 'Gagal memuatkan pilihan Tahun/Kelas: ' + (err.response?.data?.message || err.message)
      });
    }
  };

  // Reset form when modal closes
  const resetForm = () => {
    setIcNumber('');
    setStudentFound(null);
    setSelectedTahun('');
    setSelectedKelas('');
    setError('');
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Validate IC format (12 digits)
  const validateIC = (ic) => {
    const cleanIC = ic.replace(/[^\d]/g, '');
    return cleanIC.length === 12 && /^\d{12}$/.test(cleanIC);
  };

  // Handle IC input change
  const handleIcChange = (e) => {
    const value = e.target.value.replace(/[^\d-]/g, '');
    setIcNumber(value);
    setError('');
  };

  // Format IC for display (YYMMDD-PP-NNNN)
  const formatIC = (ic) => {
    const clean = ic.replace(/[^\d]/g, '');
    if (clean.length === 12) {
      return `${clean.substring(0, 6)}-${clean.substring(6, 8)}-${clean.substring(8, 12)}`;
    }
    return ic;
  };

  // Lookup student by IC
  const handleLookupStudent = async () => {
    console.log('=== ADD STUDENT MODAL: IC LOOKUP START ===');

    // Validate IC format
    const cleanIC = icNumber.replace(/[^\d]/g, '');
    console.log('1. Raw IC input:', icNumber);
    console.log('2. Cleaned IC:', cleanIC);
    console.log('3. IC type:', typeof cleanIC);
    console.log('4. IC length:', cleanIC.length);

    if (!validateIC(cleanIC)) {
      setError('No. Kad Pengenalan mestilah tepat 12 digit');
      console.log('5. IC validation FAILED');
      return;
    }
    console.log('5. IC validation PASSED');

    // Check auth token
    const token = localStorage.getItem('accessToken');
    console.log('6. Auth token present:', !!token);
    console.log('7. Auth token length:', token ? token.length : 0);
    if (token) {
      console.log('8. Token first 20 chars:', token.substring(0, 20) + '...');
    }

    setIsLookingUp(true);
    setError('');
    setStudentFound(null);

    try {
      console.log('9. About to call API with IC:', cleanIC);
      console.log('10. Calling studentUniformAPI.lookupStudent() with enrollYear:', enrollYear);

      const response = await studentUniformAPI.lookupStudent(cleanIC, enrollYear);

      console.log('11. API call succeeded!');
      console.log('12. Response:', response);

      if (response.success && response.data) {
        console.log('13. Student FOUND:', response.data);
        setStudentFound(response.data);
        setError('');
      } else {
        console.log('14. Student NOT FOUND (success=false or no data)');
        setError('No. KP tidak dijumpai dalam Senarai Induk Pelajar KPMKR.');
        setStudentFound(null);
      }
    } catch (err) {
      console.error('=== ADD STUDENT MODAL: ERROR ===');
      console.error('Full error object:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      console.error('Error response status:', err.response?.status);
      console.error('Error response statusText:', err.response?.statusText);
      console.error('Error response data:', err.response?.data);
      console.error('Error response headers:', err.response?.headers);
      console.error('Error config:', err.config);
      console.error('Error config method:', err.config?.method);
      console.error('Error config url:', err.config?.url);
      console.error('Error config baseURL:', err.config?.baseURL);
      console.error('Error config headers:', err.config?.headers);
      console.error('Error config params:', err.config?.params);
      console.error('=== END ERROR DEBUG ===');

      const errorMessage = err.response?.data?.message || err.message || 'Failed to lookup student';
      setError(errorMessage);
      setStudentFound(null);
    } finally {
      setIsLookingUp(false);
      console.log('=== ADD STUDENT MODAL: IC LOOKUP END ===');
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    // Validate all fields are filled
    if (!studentFound) {
      setError('Sila cari pelajar terlebih dahulu');
      return;
    }

    if (!selectedTahun) {
      setError('Sila pilih Tahun');
      return;
    }

    if (!selectedKelas) {
      setError('Sila pilih Kelas');
      return;
    }

    // Prepare student data for grid
    const newStudent = {
      importAction: 'ADD', // Required by importValidatedStudents action
      studentId: studentFound.studentId,
      name: studentFound.name,
      noKP: icNumber.replace(/[^\d]/g, ''),
      citizenStatus: studentFound.citizenStatus,
      jantina: studentFound.jantina,
      tahun: selectedTahun,
      class: selectedKelas, // Note: Redux expects 'class' not 'kelas'
      isNewStudent: false, // Student found in database
      isEdited: true, // Mark as edited so it gets saved
      // Initialize all uniform fields as empty strings (Redux expects strings, not null)
      bajuPutih: '',
      seluarPanjang: '',
      seluarPendek: '',
      bajuKurung: '',
      kainKurung: '',
      pinafore: '',
      kasut: ''
    };

    // Call parent callback to add student to grid
    onAddStudent(newStudent);

    // Reset form and close modal
    resetForm();
    onClose();

    // Show success message
    Swal.fire({
      icon: 'success',
      title: 'Pelajar Ditambah',
      text: `${studentFound.name} telah ditambah ke dalam senarai`,
      timer: 2000,
      showConfirmButton: false
    });
  };

  // Handle Enter key press in IC input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLookupStudent();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-student-modal-overlay" onClick={handleClose}>
      <div className="add-student-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-student-modal-header">
          <h3>Tambah Pelajar</h3>
          <button className="add-student-modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="add-student-modal-body">
          {/* Step 1: IC Lookup */}
          <div className="add-student-section">
            <h4>Langkah 1: Masukkan No. Kad Pengenalan</h4>
            <div className="add-student-ic-input-group">
              <input
                type="text"
                className="add-student-ic-input"
                placeholder="Masukkan No. KP (12 digit)"
                value={icNumber}
                onChange={handleIcChange}
                onKeyPress={handleKeyPress}
                maxLength={14}
                disabled={isLookingUp}
                autoFocus
              />
              <button
                className="add-student-lookup-btn"
                onClick={handleLookupStudent}
                disabled={isLookingUp || icNumber.replace(/[^\d]/g, '').length !== 12}
              >
                {isLookingUp ? 'Mencari...' : 'Cari'}
              </button>
            </div>
            {error && <div className="add-student-error">{error}</div>}
          </div>

          {/* Step 2: Display Student Info (if found) */}
          {studentFound && (
            <>
              <div className="add-student-section">
                <h4>Langkah 2: Maklumat Pelajar</h4>
                <div className="add-student-info-grid">
                  <div className="add-student-info-item">
                    <label>Nama:</label>
                    <div className="add-student-info-value">{studentFound.name}</div>
                  </div>
                  <div className="add-student-info-item">
                    <label>No. Kad Pengenalan:</label>
                    <div className="add-student-info-value">{formatIC(studentFound.ic)}</div>
                  </div>
                  <div className="add-student-info-item">
                    <label>Jantina:</label>
                    <div className="add-student-info-value">
                      {studentFound.jantina === 'L' ? 'Lelaki' : studentFound.jantina === 'P' ? 'Perempuan' : studentFound.jantina}
                    </div>
                  </div>
                  <div className="add-student-info-item">
                    <label>Status Warganegara:</label>
                    <div className="add-student-info-value">{studentFound.citizenStatus}</div>
                  </div>
                </div>

                {/* Current Enrollment Info */}
                {studentFound.currentEnrollment && (
                  <div className="add-student-enrollment-info" style={{
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: '#e3f2fd',
                    borderLeft: '4px solid #2196F3',
                    borderRadius: '4px'
                  }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#1976D2' }}>
                      📚 Pendaftaran Semasa ({studentFound.currentEnrollment.enrollYear})
                    </h5>
                    <div className="add-student-info-grid">
                      <div className="add-student-info-item">
                        <label>Sekolah:</label>
                        <div className="add-student-info-value">
                          {studentFound.currentEnrollment.schoolCode} - {studentFound.currentEnrollment.schoolName}
                        </div>
                      </div>
                      <div className="add-student-info-item">
                        <label>Tahun:</label>
                        <div className="add-student-info-value">{studentFound.currentEnrollment.tahun}</div>
                      </div>
                      {studentFound.currentEnrollment.class && (
                        <div className="add-student-info-item">
                          <label>Kelas:</label>
                          <div className="add-student-info-value">{studentFound.currentEnrollment.class}</div>
                        </div>
                      )}
                      <div className="add-student-info-item">
                        <label>Status:</label>
                        <div className="add-student-info-value">
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '3px',
                            backgroundColor: studentFound.currentEnrollment.status === 'ACTIVE' ? '#4CAF50' : '#FFC107',
                            color: 'white',
                            fontSize: '0.85em'
                          }}>
                            {studentFound.currentEnrollment.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 3: Select Tahun and Kelas */}
              <div className="add-student-section">
                <h4>Langkah 3: Pilih Tahun dan Kelas untuk Sekolah Anda</h4>
                <div className="add-student-form-grid">
                  <div className="add-student-form-item">
                    <label>Tahun: <span className="required">*</span></label>
                    <select
                      className="add-student-select"
                      value={selectedTahun}
                      onChange={(e) => setSelectedTahun(e.target.value)}
                    >
                      <option value="">-- Pilih Tahun --</option>
                      {tahunOptions.map((tahun) => (
                        <option key={tahun} value={tahun}>
                          {tahun}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="add-student-form-item">
                    <label>Kelas: <span className="required">*</span></label>
                    <select
                      className="add-student-select"
                      value={selectedKelas}
                      onChange={(e) => setSelectedKelas(e.target.value)}
                    >
                      <option value="">-- Pilih Kelas --</option>
                      {kelasOptions.map((kelas) => (
                        <option key={kelas} value={kelas}>
                          {kelas}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="add-student-modal-footer">
          <button className="add-student-btn-cancel" onClick={handleClose}>
            Batal
          </button>
          <button
            className="add-student-btn-confirm"
            onClick={handleSubmit}
            disabled={!studentFound || !selectedTahun || !selectedKelas}
          >
            Sahkan & Tambah
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStudentModal;
